const uuid = require('uuid');

module.exports = function OT({ state, randomId } = {}) {
  randomId = randomId || (() => uuid.v4().substring(24));

  /* istanbul ignore next */
  if (state == null) {
    state = {
      id: randomId(),
      version: 0,
      documents: {
        root: {
          // the "root" document is a special document that is the parent of "./"
          docId: 'root',
          docType: 'directory',
          children: {}
        }
      }
    };
  }

  const lastSync = {
    id: state.id,
    version: state.version
  };

  const registeredDocIds = { root: true };
  let danglingDocIds = {};

  const applyOp = function(op, registerOnAdd) {
    switch (op.type) {
      case 'add':
        return applyAdd(op, registerOnAdd);
      case 'unlink':
        return applyUnlink(op);
      case 'rename':
        return applyRename(op);
      case 'insert':
        return applyInsert(op);
      case 'remove':
        return applyRemove(op);
    }
  };

  const applyAdd = function(op, registerOnAdd) {
    let doc;
    const parent = state.documents[op.parentId];
    if (!parent || !registeredDocIds[op.parentId]) {
      return;
    }

    state.documents[op.docId] = doc = {
      name: op.name,
      docId: op.docId,
      parentId: op.parentId,
      docType: op.docType
    };

    if (op.docType === 'directory') {
      doc.children = {};
    }

    parent.children[op.name] = op.docId;

    op.path = self.idToPath(op.docId);

    if (registerOnAdd) {
      return self.registerDocument(op.docId);
    }
  };

  const applyUnlink = function(op) {
    const current = state.documents[op.docId];
    if (!current) {
      return;
    }

    op.oldPath = self.idToPath(op.docId);
    op.docType = current.docType;
    op.parentId = current.parentId;

    const parent = state.documents[current.parentId];
    if (parent) {
      delete parent.children[current.name];
    }

    delete state.documents[op.docId];
    delete danglingDocIds[op.docId];
    delete registeredDocIds[op.docId];

    return markDangling(current);
  };

  const markDangling = function(base) {
    if (base && base.children) {
      for (const docId of Object.values(base.children)) {
        danglingDocIds[docId] = true;
        markDangling(state.documents[docId]);
      }
    }
  };

  const applyRename = function(op) {
    op.oldPath = self.idToPath(op.docId);

    const current = state.documents[op.docId];
    if (!current) {
      return;
    }

    const oldParent = state.documents[current.parentId];
    if (oldParent) {
      delete oldParent.children[current.name];
    }
    op.oldParentId = current.parentId;

    current.name = op.newName;
    const newParent = state.documents[op.newParentId];
    if (newParent) {
      // If we're renaming over top of an existing file, delete the old one
      const existingFileId = newParent.children[current.name];
      if (existingFileId) {
        delete state.documents[existingFileId];
      }

      newParent.children[current.name] = current.docId;
      current.parentId = newParent.docId;
    }

    op.newPath = self.idToPath(op.docId);
    return (op.docType = current.docType);
  };

  const applyInsert = function(op) {
    // do not apply insert on unregistered documents, we don't care
    if (!registeredDocIds[op.docId]) {
      return;
    }

    const origContent = state.documents[op.docId].content;
    state.documents[op.docId].content =
      origContent.substring(0, op.position) +
      op.text +
      origContent.substring(op.position);

    op.path = self.idToPath(op.docId);
    return (op.content = state.documents[op.docId].content);
  };

  const applyRemove = function(op) {
    if (!registeredDocIds[op.docId]) {
      return;
    }

    const origContent = state.documents[op.docId].content;
    state.documents[op.docId].content =
      origContent.substring(0, op.position) +
      origContent.substring(op.position + op.text.length);

    op.path = self.idToPath(op.docId);
    return (op.content = state.documents[op.docId].content);
  };

  const normalizedPath = function(path) {
    if (path === '.') {
      return './';
    }
    if (!path.startsWith('./')) {
      return `./${path}`;
    }
    return path;
  };

  const updateNameConflict = function(localOp, remoteOp, nameConflictHandler) {
    // make it work with both add and rename
    const oldName = localOp.name || localOp.newName;
    const newName = `${oldName}_conflict_${randomId()}`;
    if (localOp.name) {
      localOp.name = newName;
    } else {
      localOp.newName = newName;
    }

    const tmpOpList = {
      version: state.version,
      ops: [
        {
          type: 'rename',
          docId: localOp.docId,
          newName,
          newParentId: localOp.parentId || localOp.newParentId
        }
      ]
    };

    return nameConflictHandler(tmpOpList);
  };

  const sortOps = function(localOp, remoteOp) {
    if (localOp.position < remoteOp.position) {
      return [localOp, remoteOp];
    }

    return [remoteOp, localOp];
  };

  const updateInsertInsert = function(localOp, remoteOp) {
    if (localOp.position === remoteOp.position) {
      if (remoteOp.text.startsWith(localOp.text)) {
        localOp.type = 'noop';
        remoteOp.position += localOp.text.length;
        remoteOp.text = remoteOp.text.substring(localOp.text.length);
        return;
      }
      if (localOp.text.startsWith(remoteOp.text)) {
        remoteOp.type = 'noop';
        localOp.position += remoteOp.text.length;
        localOp.text = localOp.text.substring(remoteOp.text.length);
        return;
      }
    }

    const [firstOp, secondOp] = sortOps(localOp, remoteOp);

    return (secondOp.position += firstOp.text.length);
  };

  const updateInsertRemove = function(insertOp, removeOp) {
    if (removeOp.position >= insertOp.position) {
      return (removeOp.position += insertOp.text.length);
    }

    if (removeOp.position + removeOp.text.length > insertOp.position) {
      insertOp.type = 'noop';
      const overlapStart = insertOp.position - removeOp.position;
      return (removeOp.text =
        removeOp.text.substring(0, overlapStart) +
        insertOp.text +
        removeOp.text.substring(overlapStart));
    }

    return (insertOp.position -= removeOp.text.length);
  };

  const updateRemoveRemove = function(localOp, remoteOp) {
    const [firstOp, secondOp] = sortOps(localOp, remoteOp);

    const overlapStart = secondOp.position - firstOp.position;
    const overlapEnd = Math.min(
      firstOp.text.length,
      overlapStart + secondOp.text.length
    );
    const overlapLength = overlapEnd - overlapStart;
    secondOp.position = Math.max(
      firstOp.position,
      secondOp.position - firstOp.text.length
    );
    secondOp.text = secondOp.text.substring(overlapLength);
    firstOp.text =
      firstOp.text.substring(0, overlapStart) +
      firstOp.text.substring(overlapEnd);

    if (firstOp.text.length === 0) {
      firstOp.type = 'noop';
    }
    if (secondOp.text.length === 0) {
      return (secondOp.type = 'noop');
    }
  };

  const updateChangeUnlink = (changeOp) => (changeOp.type = 'noop');

  const updateRenameRename = function(localOp, remoteOp) {
    remoteOp.oldPath = localOp.newPath;
    return (localOp.type = 'noop');
  };

  const updateUnlinkUnlink = (localOp, remoteOp) =>
    (localOp.type = remoteOp.type = 'noop');

  const self = {
    id() {
      return state.id;
    },

    state() {
      return state;
    },

    lastSync() {
      return lastSync;
    },

    apply(opList, { registerOnAdd } = {}) {
      if (opList.version !== state.version) {
        return false;
      }

      for (const op of opList.ops) {
        applyOp(op, registerOnAdd);
      }

      return true;
    },

    version() {
      return state.version;
    },

    setVersion(version) {
      return (lastSync.version = state.version = version);
    },

    document(docId) {
      return state.documents[docId];
    },

    // only use this on the server!!!
    clearDangling() {
      const ret = danglingDocIds;
      danglingDocIds = {};
      return ret;
    },

    // At startup, the server has state objects for all files and directories.  The files don't have contents
    // yet, but all of the directories in the tree have their children populated.  On the client, only the
    // top-level entities exist initially.  This is called on both client and server to register files, which
    // initializes the contents, and on the client to populate the children of a directory that is being
    // expanded.
    registerDocument(docId, { base64Content, content, children } = {}) {
      const doc = state.documents[docId];

      /* istanbul ignore next: breaks frontend code coverage */
      if (doc.docType === 'directory') {
        if (children) {
          doc.children = {};
          // we need to dereference children here
          for (const name in children) {
            const child = children[name];
            doc.children[name] = child.docId;
            state.documents[child.docId] = child;
          }
        }
      } else if (base64Content) {
        state.documents[docId].base64Content = base64Content;
      } else {
        state.documents[docId].content = content || '';
      }

      return (registeredDocIds[docId] = true);
    },

    isRegistered(docId) {
      return !!registeredDocIds[docId];
    },

    registeredDocIds() {
      return registeredDocIds;
    },

    documentByPath(path) {
      path = normalizedPath(path);
      let current = state.documents.root;
      for (const token of path.split('/')) {
        if (token === '') {
          break;
        }
        current = state.documents[current.children[token]];
        if (!current) {
          return null;
        }
      }
      return current;
    },

    pathToId(path) {
      return self.documentByPath(path).docId;
    },

    idToPath(docId) {
      let current = state.documents[docId];
      if (!current) {
        return null;
      }

      if (current.name === '.') {
        return './';
      }

      let path = current.name;
      while (!path.startsWith('./')) {
        current = state.documents[current.parentId];
        if (!current) {
          return null;
        }
        path = `${current.name}/${path}`;
      }
      return path;
    },

    // these fields are auto generated by apply and we get rid of them to save some bandwidth
    stripAddedFields(opList) {
      // Remove fields added by ot.apply
      for (const op of opList.ops) {
        switch (op.type) {
          case 'add':
            delete op.path;
            break;
          case 'unlink':
            // parentId is used by filterRegistered
            delete op.oldPath;
            delete op.docType;
            break;
          case 'rename':
            // oldParentId is used by filterRegistered
            delete op.oldPath;
            delete op.newPath;
            delete op.docType;
            break;
          case 'insert':
          case 'remove':
            delete op.path;
            delete op.content;
            break;
          /* istanbul ignore next: breaks frontend coverage */
          default:
            undefined;
        }
      }
    },

    // let's remember that localOp is already applied _locally_ and remoteOp is already applied _remotely_
    // we need to update localOp given remoteOp so that it preserves the intention _remotely_
    // and we need to update remoteOp given localOp so that it preserves the intention _locally_
    updateOp(localOp, remoteOp, nameConflictHandler) {
      const differentDocId = localOp.docId !== remoteOp.docId;
      const affectedTypes = ['add', 'rename'];
      const inAffectedTypes =
        affectedTypes.indexOf(localOp.type) >= 0 &&
        affectedTypes.indexOf(remoteOp.type) >= 0;
      /* istanbul ignore next: breaks frontend coverage */
      const samePath =
        localOp.name &&
        (localOp.parentId === remoteOp.parentId ||
          localOp.parentId === remoteOp.newParentId) &&
        (localOp.name === remoteOp.name || localOp.name === remoteOp.newName);
      const sameNewPath =
        localOp.newName &&
        (localOp.newParentId === remoteOp.parentId ||
          localOp.newParentId === remoteOp.newParentId) &&
        (localOp.newName === remoteOp.name ||
          localOp.newName === remoteOp.newName);

      if (differentDocId && inAffectedTypes && (samePath || sameNewPath)) {
        updateNameConflict(localOp, remoteOp, nameConflictHandler);
        return;
      }

      if (differentDocId) {
        return;
      }

      switch (`${localOp.type}_${remoteOp.type}`) {
        case 'insert_insert':
          return updateInsertInsert(localOp, remoteOp);
        case 'insert_remove':
          return updateInsertRemove(localOp, remoteOp);
        case 'remove_insert':
          return updateInsertRemove(remoteOp, localOp);
        case 'remove_remove':
          return updateRemoveRemove(localOp, remoteOp);

        case 'insert_unlink':
          return updateChangeUnlink(localOp);
        case 'remove_unlink':
          return updateChangeUnlink(localOp);
        case 'unlink_insert':
          return updateChangeUnlink(remoteOp);
        case 'unlink_remove':
          return updateChangeUnlink(remoteOp);

        case 'rename_rename':
          return updateRenameRename(localOp, remoteOp);

        case 'unlink_unlink':
          return updateUnlinkUnlink(localOp, remoteOp);
      }
    },

    applyEdit(content, op) {
      switch (op.type) {
        case 'insert':
          return (
            content.substring(0, op.position) +
            op.text +
            content.substring(op.position)
          );
        case 'remove':
          return (
            content.substring(0, op.position) +
            content.substring(op.position + op.text.length)
          );
      }
    }
  };

  return self;
};
